import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en/translations.json';

// Same i18next setup as oclweb3/oclmap, but non-English bundles are lazy
// webpack chunks injected on demand via addResourceBundle — visitors only
// download English plus their chosen language. Adding a locale = dropping
// locales/<lang>/translations.json and registering the loader here.
export const LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'zh', label: '中文' },
];

const LOADERS = {
  es: () => import(/* webpackChunkName: "locale-es" */ './locales/es/translations.json'),
  zh: () => import(/* webpackChunkName: "locale-zh" */ './locales/zh/translations.json'),
};

// scripts/prerender.js registers happy-dom globals before importing the
// app (UserMenu/AnnouncementBanner/auth.js need real window/localStorage
// implementations, not just guards), so `typeof window` alone can't tell
// Node and browser apart here — happy-dom makes window exist in both.
// globalThis.__SSR__ is the explicit marker prerender.js sets. Server
// renders are always English regardless — there's no real client to detect
// a preference from — so the detector is skipped whenever __SSR__ is set,
// falling back to fallbackLng.
const isBrowser = typeof globalThis.__SSR__ === 'undefined';
const base = isBrowser ? i18n.use(LanguageDetector) : i18n;
const initialized = base.use(initReactI18next).init({
  fallbackLng: 'en',
  ...(isBrowser ? {} : { lng: 'en' }),
  supportedLngs: LOCALES.map(locale => locale.code),
  nonExplicitSupportedLngs: true,
  load: 'languageOnly',
  resources: {
    en: {
      translations: en
    }
  },
  ns: ['translations'],
  defaultNS: 'translations',
  interpolation: { escapeValue: false },
  detection: isBrowser ? { order: ['localStorage', 'navigator'], caches: ['localStorage'] } : undefined,
});

export const setLanguage = async (code) => {
  if (LOADERS[code] && !i18n.hasResourceBundle(code, 'translations')) {
    const bundle = await LOADERS[code]();
    i18n.addResourceBundle(code, 'translations', bundle.default || bundle);
  }
  await i18n.changeLanguage(code);
  if (isBrowser)
    document.documentElement.lang = code;
};

// The detector may have picked a non-English language before its bundle
// exists — inject it once init resolves. Note: i18n.language (the detected
// request), not resolvedLanguage — the latter falls back to 'en' until the
// lazy bundle is registered. Browser-only: in Node there's no detector, so
// this would be a redundant no-op changeLanguage('en') on every SSR render.
if (isBrowser)
  initialized.then(() => setLanguage((i18n.language || 'en').split('-')[0]));

export default i18n;
